import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api, type PageMeta } from "./api";
import type {
  Alert,
  CategoryRow,
  Column,
  DashboardData,
  DataStatus,
  Product,
  ProductDetail,
  ReconRow,
} from "./types";

export interface ListParams {
  page?: number;
  page_size?: number;
  sort?: string;
  q?: string;
  date_from?: string;
  date_to?: string;
  [col: string]: string | number | boolean | undefined;
}

export interface ListResult<T> {
  rows: T[];
  meta: PageMeta;
}

async function list<T>(path: string, params: ListParams): Promise<ListResult<T>> {
  const res = await api.get<T[]>(path, params);
  return { rows: res.data, meta: res.meta ?? { page: 1, page_size: res.data.length, total: res.data.length, pages: 1 } };
}

export const useDataStatus = () =>
  useQuery({
    queryKey: ["meta", "status"],
    queryFn: () => api.get<DataStatus>("/meta/status").then((r) => r.data),
    // polls faster while a sync (cron or manual) is actually running, so the "обновлено …" chip
    // and the admin refresh button react within a few seconds of it finishing
    refetchInterval: (query) => (query.state.data?.state === "RUNNING" ? 3_000 : 60_000),
  });

/** Admin-only: kick off an ETL sync now, on top of the scheduled cron. */
export const triggerSync = () => api.post<{ started: boolean; reason?: string }>("/sync").then((r) => r.data);

export interface SyncProgress {
  running: boolean;
  stage: string | null;
  pct: number | null;
  /** how the last manual run ended — null while running / before any run */
  outcome: "SUCCESS" | "SKIPPED" | "FAILED" | null;
}

/** Admin-only: which stage the sync WE triggered is on right now (real, not simulated) — polled
 * only while the progress modal is open. */
export const useSyncProgress = (enabled: boolean) =>
  useQuery({
    queryKey: ["sync", "progress"],
    queryFn: () => api.get<SyncProgress>("/sync/progress").then((r) => r.data),
    enabled,
    refetchInterval: enabled ? 1_000 : false,
  });

/* ---------------- users (admin only) ---------------- */

export interface WebUser {
  user_id: number;
  username: string;
  full_name: string;
  role: "manager" | "admin";
  is_active: boolean;
  created_at: string | null;
  last_login_at: string | null;
}

export const useUsers = (enabled: boolean) =>
  useQuery({
    queryKey: ["users"],
    enabled,
    queryFn: () => api.get<WebUser[]>("/users").then((r) => r.data),
  });

export const createUser = (body: { username: string; full_name: string; role: string; password: string }) =>
  api.post<WebUser>("/users", body).then((r) => r.data);
export const updateUser = (id: number, body: { full_name?: string; role?: string; is_active?: boolean }) =>
  api.patch<WebUser>(`/users/${id}`, body).then((r) => r.data);
export const resetUserPassword = (id: number, password: string) =>
  api.post(`/users/${id}/password`, { password });
export const deleteUser = (id: number) => api.delete<{ user_id: number; deleted: boolean }>(`/users/${id}`);

export const useDashboard = (trendDays = 30) =>
  useQuery({
    queryKey: ["dashboard", trendDays],
    queryFn: () => api.get<DashboardData>("/dashboard", { trend_days: trendDays }).then((r) => r.data),
  });

export const useCategories = () =>
  useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get<CategoryRow[]>("/categories").then((r) => r.data),
  });

export const useProducts = (params: ListParams) =>
  useQuery({
    queryKey: ["products", params],
    queryFn: () => list<Product>("/products", params),
    placeholderData: keepPreviousData,
  });

export const useProductColumns = () =>
  useQuery({
    queryKey: ["products", "columns"],
    queryFn: () => api.get<Column[]>("/products/columns").then((r) => r.data),
    staleTime: Infinity,
  });

export const useProductDetail = (id: string) =>
  useQuery({
    queryKey: ["product", id],
    queryFn: () => api.get<ProductDetail>(`/products/${id}`).then((r) => r.data),
    enabled: Boolean(id),
  });

export const useProductHistory = (id: string, grain: "day" | "week" | "month") =>
  useQuery({
    queryKey: ["product", id, "history", grain],
    queryFn: () =>
      api
        .get<Array<{ period: string; closing_stock: number; opening_stock: number; supply: number; shipment: number }>>(
          `/products/${id}/history`,
          { grain },
        )
        .then((r) => r.data),
    enabled: Boolean(id),
  });

export const useReconciliation = (params: ListParams & { all?: boolean }) =>
  useQuery({
    queryKey: ["reconciliation", params],
    queryFn: () => list<ReconRow>("/reconciliation", params),
    placeholderData: keepPreviousData,
  });

export const useReconSummary = () =>
  useQuery({
    queryKey: ["reconciliation", "summary"],
    queryFn: () =>
      api
        .get<{ ok: { count: number }; warning: { count: number }; error: { count: number } }>("/reconciliation/summary")
        .then((r) => r.data),
  });

export const useAlerts = (params: ListParams) =>
  useQuery({
    queryKey: ["alerts", params],
    queryFn: () => list<Alert>("/alerts", params),
    placeholderData: keepPreviousData,
  });

export const useAlertSummary = () =>
  useQuery({
    queryKey: ["alerts", "summary"],
    queryFn: () =>
      api
        .get<{ critical: number; warning: number; info: number; by_type: Array<{ issue_type: string; title: string; severity: string; count: number }> }>(
          "/alerts/summary",
        )
        .then((r) => r.data),
  });

export const useAnalytics = (grain: "daily" | "weekly" | "monthly", params: ListParams) =>
  useQuery({
    queryKey: ["analytics", grain, params],
    queryFn: () => list<Record<string, unknown>>(`/analytics/${grain}`, params),
    placeholderData: keepPreviousData,
  });

export const useAnalyticsColumns = (grain: "daily" | "weekly" | "monthly") =>
  useQuery({
    queryKey: ["analytics", grain, "columns"],
    queryFn: () => api.get<Column[]>(`/analytics/${grain}/columns`).then((r) => r.data),
    staleTime: Infinity,
  });
